import { LoadingOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Col, Input, Row, Select } from "antd";

import {
  ICountry,
  IJobGrade,
  IModal,
  IShortTermIncentive,
  IShortTermPlan,
} from "@types";

import { showSuccessPopup, sortedByName } from "@utils";

import {
  ErrorServices,
  useCreateShortTermIncentiveMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useFetchAllShortTermPlansQuery,
  useUpdateShortTermIncentiveMutation,
} from "@services";

import { useTypedSelector } from "@hooks";
import CustomModal from "@/components/customModel";

const { Option } = Select;

interface IFormValues {
  grade_id: number | "All Grades" | null;
  country_id: number | "ALL" | null;
  plan: { id: number; name: string } | null;
  is_all_grade: boolean;
  is_global: boolean;
  is_percentage: boolean;
  is_basic_pay: boolean;
  value: number | null;
  all_plan_grades?: boolean;
  all_plan_countries?: boolean;

  sub_job_function_id: number | null;
  all_plan_sub_functions: boolean;
}

interface IAddShortTermIncentive extends IModal {
  selectedShortTermIncentive: IShortTermIncentive | null;
  setSelectedShortTermIncentive: React.Dispatch<
    React.SetStateAction<IShortTermIncentive | null>
  >;
  isEdit?: boolean;
}

const AddShortTermIncentive: FC<IAddShortTermIncentive> = ({
  isVisible,
  setIsVisible,
  selectedShortTermIncentive,
  setSelectedShortTermIncentive,
  isEdit,
}) => {
  const { plan_id } = useParams<{ plan_id?: string }>();

  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;

  const [shortTermIncentive, setShortTermIncentive] =
    useState<IFormValues>({
      grade_id: null,
      country_id: null,
      plan: null,
      is_all_grade: false,
      is_global: false,
      is_percentage: false,
      is_basic_pay: false,
      value: null,

      sub_job_function_id: null,
      all_plan_sub_functions: false,
    });

  const { data: plansData, isLoading: isFetchingPlans } =
    useFetchAllShortTermPlansQuery({ company_id });

  const { data: plans } = plansData || {};

  const selectedPlan = plans?.find(
    (p: any) => p.id === shortTermIncentive?.plan?.id
  );

  const selectedPlanSubFunctions =
    selectedPlan?.job_sub_function ||
    selectedPlan?.sub_job_functions ||
    [];

  const { data: jobGradesData, isLoading: isFetchingGrades } =
    useFetchAllCompanyJobGradesQuery({ company_id });

  const { data: jobGrades } = jobGradesData || {};

  const { data: countriesData, isLoading: isFetchingCountries } =
    useFetchCompanyCountriesQuery({
      company_id,
    });

  const { data: countries } = countriesData || {};

  const [createShortTermIncentive, { isLoading: isCreating }] =
    useCreateShortTermIncentiveMutation();

  const [updateShortTermIncentive, { isLoading: isUpdating }] =
    useUpdateShortTermIncentiveMutation();

  const [planGrades, setPlanGrades] = useState<IJobGrade[]>([]);
  const [planCountries, setPlanCountries] = useState<ICountry[]>([]);
  const [planCountriesForPlan, setPlanCountriesForPlan] = useState<ICountry[]>(
    []
  );



  useEffect(() => {
    if (plan_id && !selectedShortTermIncentive) {
      const plan = plans?.find((x: any) => x.id === +plan_id);

      setShortTermIncentive(prev => ({
        ...prev,
        plan,
        is_global: plan?.is_global,
        is_all_grade: plan?.is_all_grade,
      }));

      setPlanGrades(
        plan?.is_all_grade
          ? jobGrades || []
          : plan?.grade_obj || []
      );
    }
  }, [plan_id, plans, selectedShortTermIncentive, countries, jobGrades]);

  useEffect(() => {
    if (selectedShortTermIncentive?.id && isEdit) {
      const {
        country,
        grade_obj,
        plan,
        is_basic_pay,
        is_percentage,
        value,
        all_plan_grades,
        all_plan_countries,
      } = selectedShortTermIncentive;

      const selectedPlan = plans?.find(
        (x: any) => x.id === +plan?.id
      );

      const planGrades =
        selectedPlan?.grade_obj?.length
          ? selectedPlan.grade_obj
          : (jobGrades || []).filter((jg: any) =>
            selectedPlan?.grades?.includes(jg.grade)
          );

      const planCountries = (jobGrades || [])
        ?.filter((gradeObj: any) =>
          planGrades?.some((pg: any) => pg.id === gradeObj.id)
        )
        .flatMap((gradeObj: any) => gradeObj.countries || [])
        .filter(
          (c, i, self) =>
            i === self.findIndex(sc => sc.id === c.id)
        );

      const filteredCountries = all_plan_grades
        ? planCountries
        : jobGrades?.find(
          (item: any) => item?.id === grade_obj?.id
        )?.countries || [];

      setShortTermIncentive({
        plan: { id: plan?.id, name: plan?.name },

        grade_id: all_plan_grades
          ? "All Grades"
          : grade_obj?.id,

        value,

        is_all_grade: all_plan_grades,
        is_basic_pay,
        is_percentage,

        is_global: all_plan_countries,

        country_id: all_plan_countries
          ? "ALL"
          : country?.id || null,

        sub_job_function_id:
          selectedShortTermIncentive?.sub_job_function?.id ||
          selectedShortTermIncentive?.job_sub_function?.[0]?.id ||
          null,

        all_plan_sub_functions:
          selectedShortTermIncentive?.all_plan_sub_functions ||
          false,
      });

      setPlanGrades(planGrades);

      const filteredCountriesTwo =
        filteredCountries?.filter(gc =>
          selectedPlan?.countries?.some(
            (pc: any) => pc.id === gc?.id
          )
        );

      setPlanCountries(filteredCountriesTwo);
      setPlanCountriesForPlan(filteredCountriesTwo);
    } else {
      setSelectedShortTermIncentive(null);

      setShortTermIncentive({
        grade_id: null,
        country_id: null,
        plan: null,
        is_all_grade: false,
        is_global: false,
        is_percentage: false,
        is_basic_pay: false,
        value: null,
        sub_job_function_id: null,
        all_plan_sub_functions: false,
      });

      setPlanGrades([]);
      setPlanCountries([]);
      setPlanCountriesForPlan([]);
    }
  }, [selectedShortTermIncentive, isEdit, plans, jobGrades]);

  const handleSubmit = async () => {
    try {
      const {
        country_id,
        is_global,
        is_all_grade,
        grade_id,
        plan,
      } = shortTermIncentive;

      const body = {
        ...shortTermIncentive,
        country_id: is_global ? null : country_id,
        grade_id: is_all_grade ? null : grade_id,
        plan: plan?.id,
        all_plan_grades: is_all_grade,
        all_plan_countries: is_global,
      };

      delete body?.is_all_grade;
      delete body?.is_global;

      if (selectedShortTermIncentive && isEdit) {
        await editShortTermIncentive(body);
      } else {
        await addShortTermIncentive(body);
      }

      setIsVisible(false);

      showSuccessPopup({
        title: `Short Term Incentive ${selectedShortTermIncentive
          ? "Updated!"
          : "Created!"
          }`,
        desc: `You have successfully ${selectedShortTermIncentive
          ? "updated the"
          : "created a new"
          } STI Plan.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
    }
  };

  const filteredCountries = planCountriesForPlan?.filter(gc =>
    planCountries.some(pc => pc.id === gc?.id)
  );

  const addShortTermIncentive = async (body: any) =>
    await createShortTermIncentive({
      company_id,
      body,
    }).unwrap();

  const editShortTermIncentive = async (body: any) => {
    await updateShortTermIncentive({
      company_id,
      id: selectedShortTermIncentive?.id,
      body,
    }).unwrap();
  };

  return (
    <CustomModal
      width="w-6/12"
      footer={
        <>
          <button
            className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>

          <button
            className={`bg-revampBlue text-white font-medium text-lg py-2 px-14 rounded-md ${isCreating || isUpdating
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
            disabled={
              !shortTermIncentive?.value ||
              !shortTermIncentive?.plan ||
              !shortTermIncentive.country_id ||
              !shortTermIncentive.grade_id
            }
            onClick={handleSubmit}
          >
            {isCreating || isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : isEdit ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </>
      }
      title={`${isEdit ? "Update" : "Create"} a Short Term Incentive`}
      isVisible={isVisible}
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Plan Name
            </label>

            <Select
              className="custom-select"
              disabled={!!selectedShortTermIncentive || !!plan_id}
              loading={isFetchingPlans}
              size="large"
              showSearch={false}
              showArrow
              value={shortTermIncentive?.plan?.name || undefined}
              placeholder="Select plan from here..."
              onChange={(serializedPlan: any) => {
                const parsedPlan =
                  JSON.parse(serializedPlan);

                const {
                  id,
                  grade_obj,
                  grades,
                  name,
                  countries,
                  is_all_grade,
                } = parsedPlan as IShortTermPlan;

                const normalizedGrades =
                  grade_obj?.length
                    ? grade_obj
                    : (jobGrades || []).filter((jg: any) =>
                      grades?.includes(jg.grade)
                    );

                setShortTermIncentive(prev => ({
                  ...prev,
                  plan: { id, name },
                  is_global: !countries.length,
                  is_all_grade,
                  grade_id: null,
                  country_id: null,
                }));

                setPlanGrades(normalizedGrades);
                setPlanCountriesForPlan(countries);
                setPlanCountries([]);
              }}
            >
              {sortedByName(plans)?.map(
                (plan: IShortTermPlan) => (
                  <Option
                    key={plan?.id}
                    value={JSON.stringify(plan)}
                  >
                    {plan?.name}
                  </Option>
                )
              )}
            </Select>
          </Col>



          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Grade
            </label>

            <Select
              className="custom-select"
              disabled={
                !shortTermIncentive?.plan?.name ||
                planGrades?.length === 0
              }
              value={shortTermIncentive?.grade_id || undefined}
              loading={
                selectedShortTermIncentive &&
                isFetchingGrades
              }
              showArrow
              size="large"
              showSearch={false}
              placeholder="Select grade from here..."
              optionLabelProp="label"
              onChange={grade_id => {
                if (grade_id === "All Grades") {
                  setShortTermIncentive(prev => ({
                    ...prev,
                    grade_id: "All Grades",
                    is_all_grade: true,
                    country_id: null,
                  }));

                  const planCountries = (jobGrades || [])
                    .filter((gradeObj: any) =>
                      planGrades.some(
                        (pg: any) => pg.id === gradeObj.id
                      )
                    )
                    .flatMap(
                      (gradeObj: any) =>
                        gradeObj.countries || []
                    )
                    .filter(
                      (c, i, self) =>
                        i ===
                        self.findIndex(sc => sc.id === c.id)
                    );

                  setPlanCountries(planCountries);

                  return;
                }

                setShortTermIncentive(prev => ({
                  ...prev,
                  grade_id,
                  is_all_grade: false,
                  country_id: null,
                }));

                const findCountries = jobGrades?.find(
                  (item: any) => item.id === grade_id
                );

                if (findCountries?.countries?.length) {
                  setPlanCountries(
                    findCountries.countries
                  );
                }
              }}
            >
              <Option
                key="All Grades"
                value="All Grades"
              >
                All Grades
              </Option>

              {planGrades?.map((grade: IJobGrade) => (
                <Option
                  key={grade.id}
                  value={grade.id}
                  label={`${grade.grade}`}
                >
                  {grade?.grade}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>


        <Row justify="space-between">
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Sub Job Function
          </label>
          <Select
            className="custom-select"
            size="large"
            value={shortTermIncentive.sub_job_function_id || undefined}
            onChange={sub_job_function_id =>
              setShortTermIncentive(prev => ({
                ...prev,
                sub_job_function_id,
              }))
            }
          >
            {selectedPlanSubFunctions.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>

          <Checkbox
          className="mt-5"
            checked={shortTermIncentive.all_plan_sub_functions}
            onChange={e =>
              setShortTermIncentive(prev => ({
                ...prev,
                all_plan_sub_functions: e.target.checked,
              }))
            }
          >
            All Plan Sub Functions
          </Checkbox>
        </Row>

        <Row justify="space-between">
          <Col span={24}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Country
            </label>

            <Select
              className="custom-select"
              disabled={
                !shortTermIncentive.grade_id ||
                planCountries?.length === 0
              }
              loading={
                shortTermIncentive?.is_global &&
                isFetchingCountries
              }
              value={
                shortTermIncentive?.country_id ||
                undefined
              }
              showArrow
              size="large"
              showSearch={false}
              placeholder="Select country from here..."
              onChange={(country_id: any) => {
                if (country_id === "ALL") {
                  setShortTermIncentive(
                    (prev: any) => ({
                      ...prev,
                      country_id: "ALL",
                      is_global: true,
                    })
                  );

                  return;
                }

                setShortTermIncentive(prev => ({
                  ...prev,
                  country_id,
                  is_global: false,
                }));
              }}
            >
              {filteredCountries?.length > 0 && (
                <Option key="ALL" value="ALL">
                  All Countries
                </Option>
              )}

              {filteredCountries?.map(
                ({ id, name }: ICountry) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Value
            </label>

            <Input
              name="value"
              size="large"
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md"
              value={shortTermIncentive?.value || ""}
              placeholder="Enter value here..."
              onChange={event =>
                setShortTermIncentive(prev => ({
                  ...prev,
                  value: +event?.target.value,
                }))
              }
            />
          </Col>
        </Row>

        <Row justify="space-between" className="my-4">
          <Col span={11}>
            <Checkbox
              checked={
                shortTermIncentive?.is_percentage
              }
              onChange={event =>
                setShortTermIncentive(prev => ({
                  ...prev,
                  is_percentage:
                    event.target.checked,
                }))
              }
            >
              <span className="font-medium text-lg text-themeInputTitleColor">
                Percentage
              </span>
            </Checkbox>
          </Col>

          <Col span={11}>
            <Checkbox
              checked={
                shortTermIncentive?.is_basic_pay
              }
              onChange={event =>
                setShortTermIncentive(prev => ({
                  ...prev,
                  is_basic_pay:
                    event.target.checked,
                }))
              }
            >
              <span className="font-medium text-lg text-themeInputTitleColor">
                Basic Pay
              </span>
            </Checkbox>
          </Col>
        </Row>
      </>
    </CustomModal>
  );
};

export default AddShortTermIncentive;